import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PasswordIcon } from '@phosphor-icons/react/dist/csr/Password';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Input } from '../../design-system/primitives/Input/Input';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
  api,
  type ApiUser,
} from '../../lib/api/client';
import styles from './ChangePasswordScreen.module.css';

/**
 * Change your own password — `PUT /api/v1/profile/password`.
 *
 * Two callers, one component — the plan's own File Structure line for this
 * file: "Voluntary and forced".
 *
 * - **Forced.** `App.tsx` renders this, and nothing else, the moment
 *   `session.user.must_change_password` is true — the state an administrator
 *   puts an account into by issuing a temporary password. See that module's
 *   docblock for why the redirect itself carries no authority.
 * - **Voluntary.** The same component, reached from a control in
 *   `SettingsScreen` (`onChangePassword`) when `activePath === '/password'` —
 *   for the person who thinks their password may be compromised and wants to
 *   change it before anyone forces the question. `SettingsScreen` (task 8) is
 *   the "change my own details" screen; this one stays deliberately narrower
 *   and holds no `full_name`/`locale`/`theme`/`density` fields of its own —
 *   the two screens are reached separately and never merge into one form.
 *   Both callers pass the identical `{ onChanged }` prop and get the
 *   identical standalone rendering; only what `App.tsx` does with the
 *   resulting `activePath` differs (`/profile` on a voluntary success, the
 *   shell falling through to whatever it would otherwise show on a forced
 *   one — see that module).
 *
 * It renders OUTSIDE `AppShell`, the same way `LoginPage` does (task 24
 * §11.2): a person who has not yet chosen a new password has nowhere the nav
 * could usefully send them, and every one of those links would 403 under
 * `MustChangePassword` on the API side. It owns its own `<h1>` for the same
 * reason `LoginPage` does — it is the whole page while it is up.
 *
 * ## Getting OUT of the forced screen
 *
 * `api.changePassword` resolves to the UPDATED user resource, with
 * `must_change_password: false` — the same shape `api.me()` and
 * `api.updateProfile()` return, because all of them go through this client's
 * `send<T>()`. `session.user` does not refresh itself, so a success here that
 * only closed a local "saved" flag would leave `App.tsx` reading the STALE
 * flag forever and re-rendering this exact screen on every subsequent render
 * — a forced flow nobody can leave. `onChanged` exists to be `signIn` (task
 * 8's `SettingsScreen` sets the same precedent with `onSaved`):
 * `SessionControls.signIn` applies the server's response to `session.user`
 * AND to the appearance store in one call, and `App.tsx`'s flag check reads
 * `session.user` on the very next render — so the shell that comes back
 * already has working navigation, without a second round trip to `/auth/me`.
 *
 * ## The confirmation field is a client-side courtesy, not a control
 *
 * `api.changePassword(currentPassword, password)` sends the SAME string
 * twice — `password` and `password_confirmation` — so the server's `confirmed`
 * rule can never disagree with what this screen already checked. The
 * confirmation input exists so a typo is caught before it leaves the browser,
 * not because the server needs a second copy to compare against; the server's
 * own `different:current_password` and `min:12` rules are the ones that
 * actually gate what the new password may be, and this screen never repeats
 * them client-side. See client.ts's context note 2: the API enforces the
 * twelve-character minimum, not this form.
 *
 * ## 422 renders the server's own words; anything else is one generic alert
 *
 * Identical discipline to `SettingsScreen`: `error.details` already carries
 * the account's own language (part 2a), so a validation failure — a current
 * password that was wrong, a new one too short or identical to the old one —
 * is shown verbatim, per field. Every other failure renders as the one
 * `role="alert"` region, with nothing the person typed discarded.
 */

export interface ChangePasswordScreenProps {
  /**
   * Called with the server's response once the change has actually landed.
   * See the docblock above — this is what lets the running application (not
   * just the database) learn that `must_change_password` is now false.
   */
  onChanged: (user: ApiUser) => void;
  /**
   * Renders a Cancel control when provided. `App.tsx` passes this on the
   * VOLUNTARY render only, and deliberately omits it on the forced one.
   *
   * Review finding, round 2: this screen renders standalone with no shell
   * and this application has no router — `App.tsx`'s own docblock says so
   * ("a single active path in state rather than a router"), and nothing in
   * `src/app/` or `src/app/shell/` ever calls `pushState` or touches the
   * URL (`AppShell.tsx`'s nav handler explicitly calls `preventDefault` "to
   * keep the fragment out of the URL"). So Browser Back has no in-app
   * history entry to return to on EITHER render, and the only escape
   * without this prop is a full reload — which reinitialises `activePath`
   * to `'/'` and lands on the dashboard, not back where the person started.
   * A voluntary visitor who changes their mind needs an explicit way out;
   * a FORCED one does not get one, because having no escape is the point
   * (decision S-1) — the account must change its password before doing
   * anything else, and a cancel control would just be a more comfortable
   * route to the same wall of 403s.
   */
  onCancel?: () => void;
}

type FieldKey = 'current' | 'new' | 'confirm';

/** The server's two validated fields, mapped onto this screen's three inputs.
 *  There is no server-side field for `confirm` — it is never sent (see the
 *  component docblock) — so a `password` failure (too short, identical to the
 *  current one) is shown under the NEW password input, which is the field it
 *  is actually about. */
const FIELD_MAP: Readonly<Record<string, FieldKey>> = {
  current_password: 'current',
  password: 'new',
};

/** First message per field, matching `SettingsScreen.tsx`'s `mapDetails`. */
function mapDetails(details: Readonly<Record<string, string[]>>): Partial<Record<FieldKey, string>> {
  const out: Partial<Record<FieldKey, string>> = {};
  for (const [field, messages] of Object.entries(details)) {
    const key = FIELD_MAP[field];
    if (key === undefined) continue;
    const [first] = messages;
    if (first !== undefined) out[key] = first;
  }
  return out;
}

export function ChangePasswordScreen({ onChanged, onCancel }: ChangePasswordScreenProps) {
  const { t } = useTranslation();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [missing, setMissing] = useState<readonly FieldKey[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /** Read inside the handler: two clicks in one React batch both see `false`. */
  const inFlight = useRef(false);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case TOO_MANY_REQUESTS_CODE:
        return t('auth.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('changePassword.failed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    // FIRST statement: everything below can throw, and a default-action submit
    // on a form with no method is a GET that would put every password in the
    // URL.
    event.preventDefault();

    if (inFlight.current) return;

    // Cleared HERE, before either early-return check below, not only on the
    // way into the try block. Without this, a server field message from a
    // PRIOR failed attempt stayed on screen under a field the person had
    // already fixed, while a fresh client-side block (a blank field, a new
    // mismatch) rendered its own message above it — two messages about two
    // different submissions, one of them describing a request that no
    // longer exists.
    setFieldErrors({});
    setGeneralFailure(null);

    const empty: FieldKey[] = [];
    if (currentPassword === '') empty.push('current');
    if (newPassword === '') empty.push('new');
    if (confirmPassword === '') empty.push('confirm');
    if (empty.length > 0) {
      setMissing(empty);
      return;
    }
    setMissing([]);

    // Caught here so the person is told immediately; the server checks it too
    // (via `password_confirmation`, sent identical to `password` — see the
    // docblock), because a client is not a control.
    if (newPassword !== confirmPassword) {
      setGeneralFailure(t('changePassword.mismatch'));
      return;
    }

    inFlight.current = true;
    setSubmitting(true);

    try {
      const updated = await api.changePassword(currentPassword, newPassword);

      // Never left sitting in state once the API has confirmed them — the
      // one moment a credential this screen held is guaranteed no longer
      // needed for anything, success or retry.
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // See the prop docblock: without this, `session.user` (App.tsx's copy)
      // keeps `must_change_password: true` forever, and this exact screen
      // renders again on the very next frame.
      onChanged(updated);
    } catch (error) {
      if (!(error instanceof ApiError)) {
        // A fault in our own code. The agent must be told the change did
        // nothing, and the fault must not be disguised as a credential
        // problem.
        setGeneralFailure(t('changePassword.failed'));
        throw error;
      }

      // Kept open, typing intact: nothing on this path touches state. See
      // `MfaEnrolmentScreen.tsx`'s `closeForm` docblock for why a failure is
      // the one path that must NOT clear a credential the person just typed
      // — they have no way to know which of two fields was wrong.
      //
      // The inner emptiness check matters on its own: `error.details` having
      // KEYS is not the same claim as `mapDetails` having produced any.
      // `FIELD_MAP` only recognises `current_password` and `password` — the
      // two keys `ChangePasswordRequest::rules()` actually validates today —
      // but that is a fact about the current rule set, not a guarantee this
      // client can lean on. A 422 whose `details` carried some OTHER key
      // would `continue` past every entry in `mapDetails`'s loop, leave
      // `fieldErrors` at `{}`, and — without this fallback — render nothing
      // at all: a submission that visibly did nothing, with no alert and no
      // field message to explain why.
      if (Object.keys(error.details).length > 0) {
        const mapped = mapDetails(error.details);
        if (Object.keys(mapped).length > 0) {
          setFieldErrors(mapped);
        } else {
          setGeneralFailure(describeFailure(error));
        }
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <main className={styles['page']} data-testid="changePasswordPage">
      <form
        className={styles['card']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <h1 className={styles['title']}>{t('changePassword.title')}</h1>
        {/*
          Gated on `onCancel`, the same discriminator the Cancel button below
          reads — `App.tsx` passes it on the VOLUNTARY render only. The forced
          render's claim ("an administrator gave you a temporary password") is
          true there: `must_change_password` is the state an administrator
          puts an account into. It is FALSE for a person who opened this
          screen from Settings to rotate a password nobody else touched — in
          a call centre, telling that caller an administrator reset their
          password is the kind of statement that generates a security
          escalation. Round-2 review finding.
        */}
        <p className={styles['intro']}>
          {t(onCancel === undefined ? 'changePassword.introForced' : 'changePassword.introVoluntary')}
        </p>

        {/*
          Mounted from the first frame, EMPTY — assistive technology announces
          a change inside a region it is already watching. Same shape as
          `SettingsScreen.tsx` and `MfaEnrolmentScreen.tsx`.
        */}
        <div className={styles['alert']} role="alert" aria-atomic="true">
          {generalFailure === null ? null : (
            <span className={styles['alertText']}>{generalFailure}</span>
          )}
        </div>

        <Input
          // Stable, explicit ids on exactly the three legitimate password
          // fields — not `Field`'s auto-generated `useId()` default. A
          // test-only leak check (see the test file) narrows to precisely
          // these three, rather than to every `input[type="password"]` in
          // the tree, so a REGRESSION that added a fourth password-type
          // input (a decoy, a duplicate) would still be caught rather than
          // silently treated as "one of the known fields".
          id="changePasswordCurrent"
          label={t('changePassword.fieldCurrent')}
          width="fill"
          type="password"
          autoComplete="current-password"
          required
          value={currentPassword}
          error={missing.includes('current') ? t('form.requiredField') : fieldErrors.current}
          onChange={(event) => {
            setCurrentPassword(event.target.value);
          }}
        />

        <Input
          id="changePasswordNew"
          label={t('changePassword.fieldNew')}
          width="fill"
          type="password"
          autoComplete="new-password"
          required
          value={newPassword}
          error={missing.includes('new') ? t('form.requiredField') : fieldErrors.new}
          onChange={(event) => {
            setNewPassword(event.target.value);
          }}
        />

        <Input
          id="changePasswordConfirm"
          label={t('changePassword.fieldConfirm')}
          width="fill"
          type="password"
          autoComplete="new-password"
          required
          value={confirmPassword}
          error={missing.includes('confirm') ? t('form.requiredField') : fieldErrors.confirm}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
          }}
        />

        <div className={styles['actions']}>
          <Button icon={<PasswordIcon size={16} />} type="submit" loading={submitting} className={styles['submit']}>
            {t('changePassword.submit')}
          </Button>

          {/*
            Present ONLY when the caller passed `onCancel` — see the prop
            docblock. Not `disabled`, ABSENT: a visibly disabled Cancel
            button on the forced screen would still announce "there is a
            way out, just not for you", which is not the message S-1 means
            to send.
          */}
          {onCancel === undefined ? null : (
            <Button icon={<XIcon size={16} />} type="button" variant="ghost" onClick={onCancel}>
              {t('changePassword.cancel')}
            </Button>
          )}
        </div>
      </form>
    </main>
  );
}
